<template>
  <div>
    <select-base
      ref="selectBase"
      v-model="valueInner"
      :disabled="disabled"
      :multiple="multiple"
      :label="label"
      :label-select="'nome'"
      :carregar="carregarServicos"
      :filtro-adicional="aplicarFiltroCriacao"
      :filtros="filtrosBase"
      :invalido="invalido"
      :clearable="clearable"
      @search-change="atualizarBusca"
      @input="enviarParaPai"
    />
    <div
      v-if="mostrarBotaoCriar"
      class="criar-servico-button"
      @click="abrirModalCriarServico"
    >
      <feather-icon
        icon="PlusIcon"
        size="16"
        class="mr-50"
      />
      Criar Novo Servico
    </div>
    <servico-quick-create
      ref="servicoQuickCreate"
      @servico-criado="handleServicoCriado"
    />
  </div>
</template>

<script>
import api from '@/app-modulo/comuns/servico/shared/services/api'
import '@core/scss/vue/libs/vue-select.scss'
import ServicoQuickCreate from '@/app-modulo/comercial/cadastro-proposta/shared/components/tabela-servico/ServicoQuickCreate.vue'

export default {
  name: 'ServicoSelect',
  components: {
    SelectBase: () => import('@/shared/components/select-base/SelectBase.vue'),
    ServicoQuickCreate,
  },
  props: {
    mostrarBotaoCriar: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    invalido: {
      type: Boolean,
      default: false,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    value: {
      type: [Object, Array],
      default: () => { },
    },
    label: {
      type: String,
      default: 'Servico',
    },
    filtroAdicional: {
      type: Function,
      default: () => null,
    },
    filtroPorAtividadeTributacao: {
      type: Boolean,
      default: false,
    },
    tributacaoId: {
      type: Number,
      default: 0,
    },
    atividadeId: {
      type: Number,
      default: 0,
    },
    clearable: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      internalValue: null,
      filtrosBase: [
        {
          coluna: 'Nome',
          valor: '',
          soletrado: true,
        },
      ],
      buscaAtual: '',
    }
  },
  computed: {
    valueInner: {
      set(value) {
        this.internalValue = value
      },
      get() {
        return this.internalValue
      },
    },
    aplicarFiltroCriacao() {
      return itens => {
        const baseFiltrada = typeof this.filtroAdicional === 'function'
          ? this.filtroAdicional(itens)
          : null
        const baseItens = baseFiltrada !== null && baseFiltrada !== undefined ? baseFiltrada : itens

        if (!this.mostrarBotaoCriar) {
          return baseItens
        }

        const termo = (this.buscaAtual || '').trim()
        if (!termo) {
          return baseItens
        }

        const lista = Array.isArray(baseItens) ? baseItens : []
        const existe = lista.some(item => item?.nome?.toLowerCase() === termo.toLowerCase())
        if (existe) {
          return lista
        }

        return [
          {
            id: 'novo-servico',
            nome: `+ Criar novo servico "${termo}"`,
            isOpcaoCriar: true,
            termoOriginal: termo,
          },
          ...lista,
        ]
      }
    },
  },
  watch: {
    value(value, oldValue) {
      if (value !== oldValue) {
        this.valueInner = value
      }
    },
  },
  created() {
    this.valueInner = this.value
  },
  methods: {
    carregarServicos(filtros) {
      if (this.filtroPorAtividadeTributacao) {
        return api.getServicosTributacaoAtividade(this.tributacaoId, this.atividadeId, filtros)
      }
      return api.getServicosResumidos(filtros)
    },
    enviarParaPai() {
      const selecionado = this.valueInner
      if (selecionado && selecionado.isOpcaoCriar) {
        const termo = selecionado.termoOriginal || this.buscaAtual
        this.valueInner = null
        this.$nextTick(() => {
          this.abrirModalCriarServico(termo)
        })
        return
      }
      this.$emit('input', this.valueInner)
    },
    abrirModalCriarServico(nome = '') {
      const termo = nome || this.buscaAtual
      this.$refs.servicoQuickCreate.abrir(termo)
    },
    atualizarBusca(termo) {
      const valor = termo || ''
      this.buscaAtual = valor
      this.ajustarFiltroNome(valor)
    },
    async handleServicoCriado(servicoCriado) {
      const referencia = typeof servicoCriado === 'object' ? { ...servicoCriado } : {}
      const possuiIdOficial = Boolean(referencia.id)
      const { selectBase } = this.$refs

      if (selectBase) {
        if (!Array.isArray(selectBase.itensCarregados)) {
          selectBase.itensCarregados = []
        }

        const jaExiste = possuiIdOficial
          ? selectBase.itensCarregados.some(item => Number(item?.id) === Number(referencia.id))
          : selectBase.itensCarregados.some(item => item === referencia)

        if (!jaExiste) {
          selectBase.itensCarregados.unshift(referencia)
          selectBase.filtraItens(selectBase.itensCarregados)
        }
      }

      if (this.multiple) {
        const atual = Array.isArray(this.valueInner) ? [...this.valueInner] : []
        atual.push(referencia)
        this.valueInner = atual
      } else {
        this.valueInner = referencia
      }
      this.$emit('input', this.valueInner)

      if (possuiIdOficial) {
        return
      }

      const oficial = await this.sincronizarServicoOficial(referencia)

      if (oficial) {
        this.atualizarSelecaoComOficial(oficial, referencia)
      }
    },
    ajustarFiltroNome(valor) {
      this.filtrosBase = this.filtrosBase.map(filtro => (filtro.coluna === 'Nome'
        ? { ...filtro, valor: valor || '' }
        : filtro))
    },
    encontrarServicoCorrespondente(lista, referencia, exigirIdOficial = false) {
      if (!Array.isArray(lista)) {
        return null
      }

      const idReferencia = referencia?.id
      if (idReferencia) {
        const porId = lista.find(item => Number(item?.id) === Number(idReferencia))
        if (porId) {
          return porId
        }
      }

      const nomeReferencia = (referencia?.nome || '').trim().toLowerCase()
      const departamentoReferencia = referencia?.departamento?.id
        ?? referencia?.departamentoId
        ?? referencia?.departamento_id
        ?? referencia?.departamento?.Id

      return lista.find(item => {
        if (exigirIdOficial && !item?.id) {
          return false
        }

        const nomeItem = (item?.nome || '').trim().toLowerCase()
        if (nomeItem !== nomeReferencia) {
          return false
        }

        if (!departamentoReferencia) {
          return true
        }

        const departamentoItem = item?.departamento?.id
          ?? item?.departamentoId
          ?? item?.departamento_id
          ?? item?.departamento?.Id

        return Number(departamentoItem) === Number(departamentoReferencia)
      })
    },
    async sincronizarServicoOficial(referencia) {
      const { selectBase } = this.$refs
      if (!selectBase) {
        return null
      }

      const nomeBusca = (referencia?.nome || '').trim()
      this.ajustarFiltroNome(nomeBusca)

      const tentativasMaximas = 6
      const intervaloMs = 1000

      const buscar = async tentativa => {
        if (typeof selectBase.recarregarItens === 'function') {
          await selectBase.recarregarItens()
        }

        const itens = Array.isArray(selectBase.itensCarregados) ? selectBase.itensCarregados : []
        const encontrado = this.encontrarServicoCorrespondente(itens, referencia, true)
        if (encontrado) {
          return encontrado
        }

        if (tentativa >= tentativasMaximas) {
          return null
        }

        await new Promise(resolve => setTimeout(resolve, intervaloMs))
        return buscar(tentativa + 1)
      }

      const oficial = await buscar(1)
      this.buscaAtual = ''
      this.ajustarFiltroNome('')
      return oficial
    },
    atualizarSelecaoComOficial(oficial, temporario) {
      const { selectBase } = this.$refs

      if (selectBase) {
        const itens = Array.isArray(selectBase.itensCarregados) ? selectBase.itensCarregados : []
        const indiceTemporario = itens.findIndex(item => item === temporario)

        if (indiceTemporario !== -1) {
          itens.splice(indiceTemporario, 1, oficial)
        } else {
          const jaExiste = itens.some(item => Number(item?.id) === Number(oficial.id))
          if (!jaExiste) {
            itens.unshift(oficial)
          }
        }

        selectBase.itensCarregados = itens
        selectBase.filtraItens(selectBase.itensCarregados)
      }

      if (this.multiple) {
        const atualizados = (Array.isArray(this.valueInner) ? this.valueInner : [])
          .map(item => (item === temporario ? oficial : item))
        this.valueInner = atualizados
      } else if (this.valueInner === temporario) {
        this.valueInner = oficial
      }
      this.$emit('input', this.valueInner)
    },
  },
}
</script>

<style lang="scss" scoped>
.criar-servico-button {
  margin-top: 8px;
  cursor: pointer;
  padding: 8px 12px;
  color: var(--primary) !important;
  font-weight: 500;
  font-size: 0.95rem;
  display: inline-flex;
  align-items: center;
  border-radius: 4px;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(40, 199, 111, 0.12);
    color: var(--primary) !important;
  }

  ::v-deep .feather {
    width: 16px;
    height: 16px;
  }
}
</style>
